export const FIELD_HELP: Record<string, string> = {
  "agents.defaults.workspace": "Workspace directory for agent context files and memory.",
  "agents.defaults.model":
    "Default model identifier used by the agent. Use provider-prefixed format. Examples: openai/gpt-5.1 · anthropic/claude-opus-4-1 · deepseek/deepseek-chat · minimax/MiniMax-M2.5 · openrouter/openai/gpt-5.3-codex.",
  "agents.defaults.engine": "Default agent runtime kind. Built-in: native. Plugin runtimes can register additional kinds.",
  "agents.defaults.engineConfig": "Default runtime config payload passed to the selected agent runtime.",
  "agents.defaults.thinkingDefault":
    "Default thinking effort when no session override exists. Allowed: off|minimal|low|medium|high|adaptive|xhigh.",
  "agents.defaults.models":
    "Optional per-model strategy overrides. Set agents.defaults.models.<provider/model>.params.thinking for model-specific defaults.",
  "agents.defaults.contextTokens": "Maximum model input context budget (used for token-budget pruning before provider calls).",
  "agents.defaults.reservedContextTokens":
    "Optional exact context reserve before compaction. When omitted, NextClaw uses min(10000, floor(contextTokens * 0.2)).",
  "agents.defaults.maxToolIterations": "Maximum tool calls per turn.",
  "agents.list": "List of agent runtimes hosted by one gateway process.",
  "agents.list.*.id": "Agent identifier used for routing and session partition.",
  "agents.list.*.default": "Mark this agent as the default routing target.",
  "agents.list.*.workspace": "Optional per-agent workspace override.",
  "agents.list.*.model": "Optional per-agent model override.",
  "agents.list.*.engine": "Optional per-agent runtime override.",
  "agents.list.*.engineConfig": "Optional per-agent runtime config override.",
  "agents.list.*.thinkingDefault":
    "Optional per-agent default thinking effort. Takes precedence over agents.defaults.thinkingDefault.",
  "agents.list.*.models":
    "Optional per-agent model strategy overrides. Set agents.list[].models.<provider/model>.params.thinking to override thinking by model for that agent.",
  "agents.list.*.contextTokens": "Optional per-agent input context budget override.",
  "agents.list.*.reservedContextTokens": "Optional per-agent exact context reserve before compaction.",
  "agents.list.*.maxToolIterations": "Optional per-agent max tool iterations.",
  "agents.context.bootstrap.files": "Files injected into the system prompt at startup.",
  "agents.context.bootstrap.minimalFiles": "Minimal file set used for low-context runs.",
  "agents.context.bootstrap.perFileChars": "Max chars per bootstrap file.",
  "agents.context.bootstrap.totalChars": "Max total chars across bootstrap files.",
  "agents.context.memory.enabled": "Enable memory injection from memory files.",
  "agents.context.memory.maxChars": "Max characters of memory injected per turn.",
  "agents.learningLoop.enabled": "Enable automatic background learning-loop reviews after enough main-session tool usage accumulates.",
  "agents.learningLoop.toolCallThreshold": "Minimum number of tool calls since the last learning-loop review before the next review is triggered.",
  "bindings": "Inbound routing rules: channel + account + peer -> agent.",
  "bindings.*.agentId": "Target agent id for this routing rule.",
  "bindings.*.match.channel": "Channel id to match (e.g. discord, telegram).",
  "bindings.*.match.accountId": "Optional account id matcher; '*' matches all accounts.",
  "bindings.*.match.peer.kind": "Peer kind matcher (direct/group/channel).",
  "bindings.*.match.peer.id": "Peer id matcher (user/channel/group id).",
  "session.dmScope": "DM session partition strategy (main/per-peer/per-channel-peer/per-account-channel-peer).",
  "providers.*": "Configure credentials and endpoints for this provider.",
  "providers.openai": "Leading AI models including GPT-4 and GPT-3.5.",
  "providers.*.displayName": "Optional custom label shown in UI (recommended for custom provider slots).",
  "providers.*.apiKey": "API key for this provider.",
  "providers.minimax.apiBase": "China: https://api.minimaxi.com/v1. Overseas: https://api.minimax.io/v1. Pick the base URL that matches your account region.",
  "providers.*.apiBase": "Override the provider API base URL if needed.",
  "providers.*.extraHeaders": "Extra headers to send to the provider.",
  "providers.*.wireApi": "Select API mode (auto/chat/responses) for providers that support it.",
  "providers.*.models": "Custom model ids for this provider. These are merged with built-in defaults in the UI selector.",
  "providers.*.modelConfig":
    "Optional per-model capability config. Keyed by provider-local model id. Example: { \"gpt-5.3-codex\": { thinking: { supported: [\"minimal\",\"low\",\"medium\",\"high\"], default: \"low\" }, vision: true } }",
  "search.provider": "Choose which web search provider the web_search tool will use.",
  "search.enabledProviders": "Providers that are enabled and available for use in the search settings UI.",
  "search.defaults.maxResults": "Default number of web search results returned by the agent.",
  "search.providers.bocha.apiKey": "API key for Bocha AI web search.",
  "search.providers.bocha.baseUrl": "Bocha web search endpoint.",
  "search.providers.bocha.summary": "Request summary output from Bocha when supported.",
  "search.providers.bocha.freshness": "Freshness window for Bocha search results.",
  "search.providers.bocha.docsUrl": "Documentation page for obtaining a Bocha API key.",
  "search.providers.brave.apiKey": "API key for Brave Search.",
  "search.providers.brave.baseUrl": "Brave Search endpoint.",
  "search.providers.tavily.apiKey": "API key for Tavily Search.",
  "search.providers.tavily.baseUrl": "Tavily Search endpoint.",
  "search.providers.tavily.searchDepth": "Tavily search depth. Use advanced only when you need deeper retrieval.",
  "search.providers.tavily.includeAnswer": "Ask Tavily to include a synthesized answer in the search response when supported.",
  "gateway.host": "Bind address for the gateway server.",
  "gateway.port": "Port for the gateway server.",
  "ui.enabled": "Enable the built-in UI server.",
  "ui.host": "UI host is runtime-managed and treated as read-only (always 0.0.0.0 on CLI start/restart/serve/ui/gateway).",
  "ui.port": "Port for the UI server.",
  "ui.open": "Open the browser when UI starts.",
  "ui.auth": "Optional lightweight authentication for the built-in UI.",
  "ui.auth.enabled": "Require login before accessing UI APIs and WebSocket.",
  "ui.auth.username": "Single administrator username for built-in UI login.",
  "ui.auth.passwordHash": "Stored password hash for built-in UI login.",
  "ui.auth.passwordSalt": "Stored password salt for built-in UI login.",
  "tools.web.search.apiKey": "API key for the configured web search provider.",
  "tools.web.search.maxResults": "Default number of search results.",
  "tools.exec.timeout": "Command execution timeout (seconds).",
  "tools.restrictToWorkspace": "Restrict tools to the workspace directory.",
  "secrets": "Secret resolution config. Supports env/file/exec providers and path-based secret refs.",
  "secrets.enabled": "Enable runtime secret resolution from refs.",
  "secrets.defaults": "Default provider aliases for each source (env/file/exec).",
  "secrets.providers": "Named secret providers (env/file/exec).",
  "secrets.providers.*.source": "Secret source type (env/file/exec).",
  "secrets.providers.*.prefix": "Optional env var prefix when source=env.",
  "secrets.providers.*.path": "Secret JSON file path when source=file.",
  "secrets.providers.*.command": "Command used to fetch secret JSON when source=exec.",
  "secrets.providers.*.args": "Arguments for secret command when source=exec.",
  "secrets.providers.*.cwd": "Working directory for secret command when source=exec.",
  "secrets.providers.*.timeoutMs": "Timeout in milliseconds for secret command when source=exec.",
  "secrets.refs": "Map config path -> secret reference ({source, provider?, id}).",
  "secrets.refs.*.source": "Secret source for this ref.",
  "secrets.refs.*.provider": "Optional provider alias for this ref.",
  "secrets.refs.*.id": "Secret id/key used by provider lookup.",
  "channels.*": "Configure connection and credentials for this channel.",
  "channels.telegram": "Connect with Telegram bots for instant messaging.",
  "channels.slack": "Integrate with Slack workspaces for team collaboration.",
  "channels.email": "Send and receive messages via email protocols.",
  "channels.webhook": "Receive HTTP webhooks for custom integrations.",
  "channels.discord": "Connect Discord bots to your community servers.",
  "channels.feishu": "Enterprise messaging and collaboration platform.",
  "channels.wecom": "WeCom enterprise messaging integration via callback + app API.",
  "channels.whatsapp.bridgeUrl": "WebSocket URL for the WhatsApp bridge.",
  "channels.telegram.token": "Telegram bot token.",
  "channels.telegram.proxy": "Optional HTTP proxy for Telegram.",
  "channels.telegram.ackReaction": "Emoji used for Telegram inbound acknowledgment (empty disables).",
  "channels.telegram.ackReactionScope":
    'When to send Telegram ack reactions ("off" | "group-mentions" | "group-all" | "direct" | "all").',
  "channels.telegram.accountId": "Logical account id for routing bindings.",
  "channels.telegram.dmPolicy": "DM policy for Telegram (pairing/allowlist/open/disabled).",
  "channels.telegram.groupPolicy": "Group policy for Telegram (open/allowlist/disabled).",
  "channels.telegram.groupAllowFrom": "Telegram group allowlist used when groupPolicy=allowlist.",
  "channels.telegram.streaming":
    "Telegram stream preview mode (off/partial/block/progress). Boolean true maps to partial, false maps to off.",
  "channels.telegram.requireMention": "Whether Telegram group messages require mention by default.",
  "channels.telegram.mentionPatterns": "Regex/plain mention patterns accepted in Telegram groups.",
  "channels.telegram.groups": "Per-group mention overrides for Telegram.",
  "channels.discord.token": "Discord bot token.",
  "channels.discord.allowBots": "Allow bot-authored messages to trigger replies (default: false).",
  "channels.discord.gatewayUrl": "Override Discord gateway URL.",
  "channels.discord.intents": "Gateway intents bitmask.",
  "channels.discord.proxy": "Optional HTTP(S) proxy for Discord attachment downloads.",
  "channels.discord.mediaMaxMb": "Maximum inbound Discord attachment size in MB.",
  "channels.discord.streaming":
    "Discord preview streaming mode (off/partial/block/progress). Boolean true maps to partial, false maps to off.",
  "channels.discord.draftChunk":
    "Chunking options for Discord preview streaming (min/max chars and break preference).",
  "channels.discord.draftChunk.minChars":
    "Minimum chars before emitting a Discord stream preview update (default: 200).",
  "channels.discord.draftChunk.maxChars":
    "Target max size for a Discord stream preview chunk (default: 800; clamped to textChunkLimit).",
  "channels.discord.draftChunk.breakPreference":
    "Discord stream preview break preference (paragraph/line/none).",
  "channels.discord.textChunkLimit": "Maximum outbound Discord text size per message (default: 2000).",
  "channels.discord.accountId": "Logical account id for routing bindings.",
  "channels.discord.dmPolicy": "DM policy for Discord (pairing/allowlist/open/disabled).",
  "channels.discord.groupPolicy": "Guild/channel policy for Discord (open/allowlist/disabled).",
  "channels.discord.groupAllowFrom": "Discord channel allowlist used when groupPolicy=allowlist.",
  "channels.discord.requireMention": "Whether Discord group messages require mention by default.",
  "channels.discord.mentionPatterns": "Regex/plain mention patterns accepted in Discord groups.",
  "channels.discord.groups": "Per-channel mention overrides for Discord.",
  "channels.feishu.appId": "Feishu app ID.",
  "channels.feishu.appSecret": "Feishu app secret.",
  "channels.feishu.encryptKey": "Feishu encrypt key (if enabled).",
  "channels.feishu.verificationToken": "Feishu verification token.",
  "channels.feishu.domain": "Feishu/Lark domain selection. Use 'feishu', 'lark', or a custom https:// domain.",
  "channels.feishu.allowFrom": "Allowed sender ids for direct-message routing.",
  "channels.feishu.dmPolicy": "DM policy for Feishu (pairing/allowlist/open/disabled).",
  "channels.feishu.groupPolicy": "Group policy for Feishu (open/allowlist/disabled).",
  "channels.feishu.groupAllowFrom": "Feishu group allowlist used when groupPolicy=allowlist.",
  "channels.feishu.requireMention": "Whether Feishu group messages require mention by default.",
  "channels.feishu.mentionPatterns": "Regex/plain mention patterns accepted in Feishu groups.",
  "channels.feishu.groups": "Per-group mention overrides for Feishu.",
  "channels.feishu.accounts": "Multi-account Feishu overrides keyed by logical account id.",
  "channels.dingtalk.clientId": "DingTalk client ID.",
  "channels.dingtalk.clientSecret": "DingTalk client secret.",
  "channels.wecom.corpId": "WeCom Corp ID.",
  "channels.wecom.agentId": "WeCom app agent ID.",
  "channels.wecom.secret": "WeCom app secret.",
  "channels.wecom.token": "WeCom callback token used for signature verification.",
  "channels.wecom.callbackPort": "Local HTTP port for WeCom callback endpoint.",
  "channels.wecom.callbackPath": "Callback path for WeCom URL verification and message push.",
  "channels.slack.mode": "Slack connection mode (socket or webhook).",
  "channels.slack.webhookPath": "Slack webhook path when using webhook mode.",
  "channels.slack.botToken": "Slack bot token.",
  "channels.slack.appToken": "Slack app-level token.",
  "channels.slack.allowBots": "Allow bot-authored messages to trigger replies (default: false).",
  "channels.slack.groupPolicy": "Group policy for Slack channels.",
  "channels.slack.dm.policy": "DM policy for Slack.",
  "channels.email.imapHost": "IMAP server host.",
  "channels.email.imapPort": "IMAP server port.",
  "channels.email.imapUsername": "IMAP username.",
  "channels.email.imapPassword": "IMAP password.",
  "channels.email.smtpHost": "SMTP server host.",
  "channels.email.smtpPort": "SMTP server port.",
  "channels.email.smtpUsername": "SMTP username.",
  "channels.email.smtpPassword": "SMTP password.",
  "channels.email.fromAddress": "Default From address for outgoing mail.",
  "channels.email.pollIntervalSeconds": "Polling interval in seconds.",
  "channels.email.maxBodyChars": "Max chars to read from incoming emails.",
  "channels.email.subjectPrefix": "Prefix used for reply subjects.",
  "channels.qq.appId": "QQ app ID.",
  "channels.qq.secret": "QQ app secret."
};
